/**
 * Pagamentos — a visão financeira das transações (Fase 5).
 *
 * Três blocos, na ordem de urgência de quem abre a tela:
 *
 *   1. CAUÇÕES A RENOVAR (Gap 6) — pré-autorizações na janela de renovação ou
 *      já vencidas. O sistema NÃO renova sozinho (não guardamos token de
 *      cartão — PCI): o painel renova em um clique, com token novo do cliente.
 *   2. RECONCILIAÇÃO — transações sem desfecho e webhooks com falha (a face
 *      visível da DLQ). A varredura roda em job; aqui está o relatório dela.
 *   3. A LISTA — todas as transações do tenant, filtráveis por status,
 *      finalidade e método.
 *
 * As operações por transação (capturar, estornar, baixar) vivem no detalhe da
 * RESERVA, perto do contexto que as justifica — daqui se navega para lá.
 */

import {
  METODOS_DE_PAGAMENTO,
  ROTULO_DO_METODO_DE_PAGAMENTO,
  ROTULO_DO_STATUS_DE_TRANSACAO,
  ROTULO_DO_TIPO_DE_TRANSACAO,
  STATUS_DE_TRANSACAO,
  type MetodoDePagamento,
  type StatusDeTransacao,
} from '@locaveri/shared'
import type { ColumnDef } from '@tanstack/react-table'
import { Link } from '@tanstack/react-router'
import { CreditCard, LoaderCircle, RefreshCw, ShieldAlert, TriangleAlert } from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Paginacao } from '@/components/data/paginacao'
import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { referenciaDaReserva } from '@/features/multas/api'
import {
  FINALIDADES_DE_TRANSACAO,
  ROTULO_DA_FINALIDADE,
  useCaucoesParaRenovar,
  useRelatorioDeReconciliacao,
  useSincronizarTransacao,
  useTransacoes,
  type CaucaoParaRenovar,
  type FinalidadeDeTransacao,
  type Transacao,
} from '@/features/pagamentos/api'
import {
  BadgeDeStatusDeTransacao,
  DialogoDeRenovacaoDeCaucao,
} from '@/features/pagamentos/cartao-de-pagamentos'
import { errorMessage } from '@/lib/api'
import { SEM_VALOR, formatarDataHora } from '@/lib/datas'
import { formatarMoedaDoWire } from '@/lib/dinheiro'
import { usePermissao } from '@/lib/permissoes'

const TAMANHO_DA_PAGINA = 20

export function FinanceiroPagamentosPage(): ReactElement {
  const [pagina, setPagina] = useState(1)
  const [status, setStatus] = useState<StatusDeTransacao | ''>('')
  const [finalidade, setFinalidade] = useState<FinalidadeDeTransacao | ''>('')
  const [metodo, setMetodo] = useState<MetodoDePagamento | ''>('')

  const filtros = useMemo(
    () => ({ pagina, tamanho: TAMANHO_DA_PAGINA, status, finalidade, metodo }),
    [pagina, status, finalidade, metodo],
  )
  const consulta = useTransacoes(filtros)

  const temFiltro = status !== '' || finalidade !== '' || metodo !== ''

  function limparFiltros(): void {
    setStatus('')
    setFinalidade('')
    setMetodo('')
    setPagina(1)
  }

  const colunas = useMemo<ColumnDef<Transacao>[]>(
    () => [
      {
        id: 'criadoEm',
        header: 'Criada em',
        accessorFn: (linha: Transacao) => linha.criadoEm,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm tabular-money text-fg-secondary">
            {formatarDataHora(row.original.criadoEm)}
          </span>
        ),
      },
      {
        id: 'vinculo',
        header: 'Vínculo',
        enableSorting: false,
        cell: ({ row }) => {
          const transacao: Transacao = row.original
          if (transacao.reservationId !== null) {
            return (
              <Link
                to="/reservas/$reservaId"
                params={{ reservaId: transacao.reservationId }}
                title={transacao.reservationId}
                className="whitespace-nowrap text-sm tabular-money text-foreground
                  underline-offset-4 hover:underline"
              >
                reserva {referenciaDaReserva(transacao.reservationId)}
              </Link>
            )
          }
          if (transacao.invoiceId !== null) {
            return (
              <span
                className="whitespace-nowrap text-sm tabular-money text-fg-secondary"
                title={transacao.invoiceId}
              >
                fatura #{transacao.invoiceId.slice(0, 8)}
              </span>
            )
          }
          return <span className="text-xs text-fg-muted">{SEM_VALOR}</span>
        },
      },
      {
        id: 'finalidade',
        header: 'Finalidade',
        enableSorting: false,
        cell: ({ row }) => {
          const transacao: Transacao = row.original
          return (
            <div className="min-w-0">
              <p className="whitespace-nowrap font-medium text-foreground">
                {ROTULO_DA_FINALIDADE[transacao.finalidade]}
              </p>
              <p className="truncate text-xs text-fg-secondary">
                {ROTULO_DO_TIPO_DE_TRANSACAO[transacao.tipo]}
              </p>
            </div>
          )
        },
      },
      {
        id: 'metodo',
        header: 'Método',
        enableSorting: false,
        cell: ({ row }) => {
          const transacao: Transacao = row.original
          const cartao =
            transacao.metodo === 'CARTAO_CREDITO'
              ? `${transacao.bandeira ?? 'cartão'} •••• ${transacao.ultimosQuatro ?? '????'}`
              : null
          return (
            <div className="min-w-0">
              <p className="whitespace-nowrap text-sm text-foreground">
                {ROTULO_DO_METODO_DE_PAGAMENTO[transacao.metodo]}
                {transacao.parcelas > 1 ? ` · ${String(transacao.parcelas)}×` : ''}
              </p>
              {cartao === null ? null : (
                <p className="truncate text-xs tabular-money text-fg-secondary">{cartao}</p>
              )}
            </div>
          )
        },
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => <BadgeDeStatusDeTransacao status={row.original.status} />,
      },
      {
        id: 'valor',
        header: 'Valor',
        meta: { alinhamento: 'direita' },
        // Wire de centavos: ordenar a string mentiria ("900" > "10000").
        enableSorting: false,
        cell: ({ row }) => (
          <span className="money-cell block min-w-24 text-foreground">
            {formatarMoedaDoWire(row.original.valor)}
          </span>
        ),
      },
    ],
    [],
  )

  const dados = consulta.data

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Pagamentos</h1>
        <p className="max-w-prose text-sm text-fg-secondary">
          Todas as transações do provedor: vendas, cauções e renovações. Capturar, baixar e estornar
          acontecem no detalhe da reserva — aqui o financeiro vigia o que precisa de atenção e
          navega para lá.
        </p>
      </div>

      <CartaoDeCaucoesARenovar />
      <CartaoDeReconciliacao />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="filtro-status-pagamentos">Status</Label>
          <Select
            id="filtro-status-pagamentos"
            value={status}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setStatus(STATUS_DE_TRANSACAO.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todos</option>
            {STATUS_DE_TRANSACAO.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DO_STATUS_DE_TRANSACAO[item].rotulo}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-finalidade-pagamentos">Finalidade</Label>
          <Select
            id="filtro-finalidade-pagamentos"
            value={finalidade}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setFinalidade(FINALIDADES_DE_TRANSACAO.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todas</option>
            {FINALIDADES_DE_TRANSACAO.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DA_FINALIDADE[item]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-metodo-pagamentos">Método</Label>
          <Select
            id="filtro-metodo-pagamentos"
            value={metodo}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setMetodo(METODOS_DE_PAGAMENTO.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todos</option>
            {METODOS_DE_PAGAMENTO.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DO_METODO_DE_PAGAMENTO[item]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <TabelaDeDados
        colunas={colunas}
        dados={dados?.itens ?? []}
        carregando={consulta.isPending}
        erro={consulta.isError ? consulta.error : undefined}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
        legenda="Transações de pagamento"
        estadoVazio={
          <EmptyState
            icone={CreditCard}
            titulo={temFiltro ? 'Nenhuma transação com esses filtros' : 'Nenhuma transação ainda'}
            descricao={
              temFiltro
                ? 'Ajuste os filtros para ver outros resultados.'
                : 'As cobranças nascem no detalhe da reserva — venda das diárias e pré-autorização da caução.'
            }
            acao={
              temFiltro ? (
                <Button variant="secondary" onClick={limparFiltros}>
                  Limpar filtros
                </Button>
              ) : undefined
            }
          />
        }
      />

      <Paginacao
        pagina={dados?.pagina ?? pagina}
        tamanho={dados?.tamanho ?? TAMANHO_DA_PAGINA}
        total={dados?.total ?? 0}
        aoMudarDePagina={setPagina}
        desabilitado={consulta.isFetching}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Cauções a renovar (Gap 6)
// ---------------------------------------------------------------------------

function CartaoDeCaucoesARenovar(): ReactElement {
  const podeCriar = usePermissao('payment.create')
  const consulta = useCaucoesParaRenovar()
  const [renovando, setRenovando] = useState<CaucaoParaRenovar | null>(null)

  const itens = consulta.data?.itens ?? []

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldAlert className="size-4" /> Cauções a renovar
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {consulta.isPending ? <LoadingState linhas={1} rotulo="Verificando as cauções…" /> : null}
        {consulta.isError ? (
          <ErrorState
            titulo="Não foi possível verificar as cauções"
            erro={consulta.error}
            aoTentarNovamente={() => {
              void consulta.refetch()
            }}
          />
        ) : null}

        {!consulta.isPending && !consulta.isError && itens.length === 0 ? (
          <p className="text-sm text-fg-secondary">
            Nenhuma caução na janela de renovação — os bloqueios estão todos dentro da validade.
          </p>
        ) : null}

        {itens.map((caucao) => (
          <div
            key={caucao.transacaoId}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border
              border-border px-3 py-2 text-sm"
          >
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {caucao.vencida ? (
                <Badge variant="danger">
                  <TriangleAlert aria-hidden="true" />
                  Vencida — risco descoberto
                </Badge>
              ) : (
                <Badge variant="warning">
                  <TriangleAlert aria-hidden="true" />
                  Na janela de renovação
                </Badge>
              )}
              <span className="whitespace-nowrap text-xs text-fg-secondary">
                expira em{' '}
                <span className="tabular-money">
                  {formatarDataHora(caucao.autorizacaoExpiraEm)}
                </span>
              </span>
              {caucao.reservationId === null ? null : (
                <Link
                  to="/reservas/$reservaId"
                  params={{ reservaId: caucao.reservationId }}
                  title={caucao.reservationId}
                  className="whitespace-nowrap text-xs tabular-money text-fg-secondary
                    underline-offset-4 hover:underline"
                >
                  reserva {referenciaDaReserva(caucao.reservationId)}
                </Link>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <span className="money-cell font-medium text-foreground">
                {formatarMoedaDoWire(caucao.valor)}
              </span>
              {podeCriar ? (
                <Button
                  size="sm"
                  onClick={() => {
                    setRenovando(caucao)
                  }}
                >
                  Renovar…
                </Button>
              ) : null}
            </div>
          </div>
        ))}
      </CardContent>

      {renovando === null ? null : (
        <DialogoDeRenovacaoDeCaucao
          transacaoId={renovando.transacaoId}
          valor={renovando.valor}
          aoFechar={() => {
            setRenovando(null)
          }}
        />
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Reconciliação
// ---------------------------------------------------------------------------

function CartaoDeReconciliacao(): ReactElement {
  const podeAtualizar = usePermissao('payment.update')
  const consulta = useRelatorioDeReconciliacao()
  const sincronizar = useSincronizarTransacao()

  const relatorio = consulta.data

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="size-4" /> Reconciliação
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {consulta.isPending ? <LoadingState linhas={1} rotulo="Lendo o relatório…" /> : null}
        {consulta.isError ? (
          <ErrorState
            titulo="Não foi possível ler o relatório"
            erro={consulta.error}
            aoTentarNovamente={() => {
              void consulta.refetch()
            }}
          />
        ) : null}

        {relatorio === undefined ? null : (
          <>
            <p className="text-xs text-fg-muted">
              A varredura roda em job no servidor; este é o relatório dela. Sincronizar uma
              transação percorre o mesmo caminho do webhook.
            </p>

            {relatorio.naoTerminais.length === 0 &&
            relatorio.eventosComFalha.length === 0 &&
            relatorio.orfaosNoProvedor === 0 ? (
              <p className="text-sm text-fg-secondary">
                Nada divergente: nenhuma transação parada, nenhum webhook com falha.
              </p>
            ) : null}

            {relatorio.naoTerminais.length > 0 ? (
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">
                  Transações sem desfecho (&gt; 10 min)
                </p>
                {relatorio.naoTerminais.map((linha) => (
                  <div
                    key={linha.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md
                      border border-border px-3 py-2 text-sm"
                  >
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <BadgeDeStatusDeTransacao status={linha.status} />
                      <span className="whitespace-nowrap text-xs text-fg-secondary">
                        {ROTULO_DA_FINALIDADE[linha.finalidade]} ·{' '}
                        {ROTULO_DO_METODO_DE_PAGAMENTO[linha.metodo]} · criada em{' '}
                        <span className="tabular-money">{formatarDataHora(linha.criadoEm)}</span>
                      </span>
                      {linha.reservationId === null ? null : (
                        <Link
                          to="/reservas/$reservaId"
                          params={{ reservaId: linha.reservationId }}
                          title={linha.reservationId}
                          className="whitespace-nowrap text-xs tabular-money text-fg-secondary
                            underline-offset-4 hover:underline"
                        >
                          reserva {referenciaDaReserva(linha.reservationId)}
                        </Link>
                      )}
                    </div>
                    {podeAtualizar ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={sincronizar.isPending}
                        onClick={() => {
                          sincronizar.mutate(
                            { id: linha.id },
                            {
                              onSuccess: () => {
                                toast.success('Estado convergido com o provedor.')
                              },
                              onError: (erro) => {
                                toast.error(errorMessage(erro))
                              },
                            },
                          )
                        }}
                      >
                        {sincronizar.isPending ? (
                          <LoaderCircle className="animate-spin" aria-hidden="true" />
                        ) : (
                          <RefreshCw aria-hidden="true" />
                        )}
                        Sincronizar
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}

            {relatorio.eventosComFalha.length > 0 ? (
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">
                  Webhooks com falha de processamento
                </p>
                {relatorio.eventosComFalha.map((evento) => (
                  <div
                    key={evento.id}
                    className="space-y-1 rounded-md border border-danger-fg/30 px-3 py-2 text-sm"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="min-w-0 truncate font-medium text-foreground">
                        {evento.tipoCru}
                      </span>
                      <span className="shrink-0 text-xs tabular-money text-fg-secondary">
                        {String(evento.tentativas)} tentativa(s) ·{' '}
                        {formatarDataHora(evento.recebidoEm)}
                      </span>
                    </div>
                    <p className="break-all text-xs text-fg-muted">
                      transação externa {evento.transactionIdExterno}
                      {evento.erro === null ? '' : ` — ${evento.erro}`}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}

            {relatorio.orfaosNoProvedor > 0 ? (
              <p className="text-xs text-warning-fg">
                {relatorio.orfaosNoProvedor === 1
                  ? '1 evento órfão no provedor (sem tenant identificado).'
                  : `${String(relatorio.orfaosNoProvedor)} eventos órfãos no provedor (sem tenant identificado).`}
              </p>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  )
}
